/**
 * Pre-match pick UI (06 §4): LOBBY_PICK grid of 8 (toggle 3), LOBBY_LOCK
 * (decks public, pick 1; enemies CHOOSING/LOCKED), REVEAL. DOM overlay on top
 * of the canvas; sends `lobby:pick {ids}` / `lobby:lock {id}`.
 */
import { POWERUP_POOL, powerUpById } from '../../sim/powerups.js'
import type { MatchPhase, Team } from '../../sim/types.js'
import type { LobbySnap } from '../../shared/protocol.js'
import { drawGlyph } from './glyphs.js'
import { kioskSlot } from './ui/kiosk-model.js'
import type { DisclordView, RunStateMsg } from '../../shared/protocol.js'

export interface LobbyPlayer { id: string; name: string; team: Team }

export class LobbyUI {
  private deck: string[] = []
  private lock: string | null = null
  private lastPhase: MatchPhase | null = null
  /** `removed` = the click deselected a card (08 §4: reverse tick instead of pen tick). */
  onPick: (ids: string[], removed: boolean) => void = () => {}
  onLock: (id: string) => void = () => {}
  /** 08 §4 deck card hover (paper slide). */
  onHover: () => void = () => {}

  constructor(private readonly root: HTMLElement) {
    root.style.display = 'none'
  }

  get visible(): boolean { return this.root.style.display !== 'none' }

  hide(): void { this.root.style.display = 'none'; this.lastPhase = null }

  update(phase: MatchPhase, lobby: LobbySnap | null, myId: string, myTeam: Team | null, players: LobbyPlayer[], spectator: boolean): void {
    if (!lobby || (phase !== 'LOBBY_PICK' && phase !== 'LOBBY_LOCK' && phase !== 'REVEAL')) { if (this.visible) this.hide(); return }
    if (phase !== this.lastPhase) {
      this.lastPhase = phase
      if (phase === 'LOBBY_PICK') { this.deck = []; this.lock = null }
      if (phase === 'LOBBY_LOCK') this.deck = lobby.decks[myId]?.slice() ?? this.deck
    }
    this.root.style.display = ''
    const secs = Math.ceil(lobby.ticksLeft / 60)
    const title = phase === 'LOBBY_PICK' ? `PICK 3 - ${secs}s` : phase === 'LOBBY_LOCK' ? `LOCK 1 - ${secs}s` : 'REVEAL'
    const parts: string[] = [`<div class="lobby-title">${title}</div>`]
    if (phase === 'LOBBY_PICK') {
      parts.push('<div class="lobby-grid">')
      for (const p of POWERUP_POOL) {
        const on = this.deck.includes(p.id)
        parts.push(`<button class="card ${on ? 'on' : ''}" data-pick="${p.id}" ${spectator ? 'disabled' : ''}><canvas data-glyph="${p.glyph}" width="40" height="40"></canvas><b>${p.name}</b><i>+ ${fmt(p.buff)}</i><i>- ${fmt(p.nerf)}</i></button>`)
      }
      parts.push('</div>')
      parts.push(`<div class="lobby-note">deck: ${this.deck.map((d) => powerUpById(d)?.name).join(', ') || '(default [heavy, bulwark, sprinter] on timeout)'}</div>`)
    } else {
      for (const t of [0, 1] as Team[]) {
        parts.push(`<div class="lobby-team">TEAM ${t === 0 ? 'A' : 'B'}</div><div class="lobby-row">`)
        for (const pl of players.filter((q) => q.team === t)) {
          const deck = lobby.decks[pl.id] ?? []
          const lockState = lobby.locks[pl.id]
          const mine = pl.id === myId
          parts.push(`<div class="lobby-player"><div class="lobby-name">${pl.name}${mine ? ' (you)' : ''} - ${phase === 'REVEAL' ? (powerUpById(lockState)?.name ?? '-') : lockState}</div>`)
          for (const id of deck) {
            const def = powerUpById(id)
            const chosen = phase === 'REVEAL' ? lockState === id : mine && this.lock === id
            parts.push(`<button class="card small ${chosen ? 'on' : ''}" ${mine && phase === 'LOBBY_LOCK' && !spectator ? `data-lock="${id}"` : 'disabled'}><canvas data-glyph="${def?.glyph}" width="24" height="24"></canvas><b>${def?.name ?? id}</b></button>`)
          }
          parts.push('</div>')
        }
        parts.push('</div>')
      }
    }
    this.root.innerHTML = parts.join('')
    for (const c of Array.from(this.root.querySelectorAll<HTMLCanvasElement>('canvas[data-glyph]'))) {
      const ctx = c.getContext('2d')
      if (ctx) drawGlyph(ctx, c.dataset.glyph as never, c.width / 2, c.height / 2, c.width * 0.85)
    }
    for (const b of Array.from(this.root.querySelectorAll<HTMLButtonElement>('button[data-pick]'))) {
      b.onpointerenter = () => this.onHover()
      b.onclick = () => {
        const id = b.dataset.pick!
        const removed = this.deck.includes(id)
        if (removed) this.deck = this.deck.filter((x) => x !== id)
        else if (this.deck.length < 3) this.deck.push(id)
        else return
        this.onPick(this.deck.slice(), removed)
        this.update(phase, lobby, myId, myTeam, players, spectator)
      }
    }
    for (const b of Array.from(this.root.querySelectorAll<HTMLButtonElement>('button[data-lock]'))) {
      b.onclick = () => { const id = b.dataset.lock!; this.lock = id; this.onLock(id); this.update(phase, lobby, myId, myTeam, players, spectator) }
    }
  }
}

function fmt(o: Record<string, unknown>): string {
  return Object.entries(o).map(([k, v]) => `${k.toLowerCase().replace(/_/g, ' ')} ${v}`).join(', ')
}

/** Hub loadout menu (06 §6): pick one power-up (or none); applies on next respawn. */
export class LoadoutMenu {
  onChange: (ids: string[]) => void = () => {}
  /** 12 §2 kiosk: slot 2/3 ← id, or null to clear (server-authoritative; the menu re-renders on the next disclord view). */
  onSlot: (index: 2 | 3, id: string | null) => void = () => {}
  constructor(private readonly root: HTMLElement) { root.style.display = 'none' }
  get visible(): boolean { return this.root.style.display !== 'none' }
  toggle(current: string[], disclord: DisclordView | null = null): void {
    if (this.visible) { this.root.style.display = 'none'; return }
    this.root.style.display = ''
    const parts = ['<div class="lobby-title">LOADOUT (hub, applies on next respawn)</div><div class="lobby-grid">']
    parts.push(`<button class="card ${current.length === 0 ? 'on' : ''}" data-load=""><b>NONE</b></button>`)
    for (const p of POWERUP_POOL) parts.push(`<button class="card ${current.includes(p.id) ? 'on' : ''}" data-load="${p.id}"><canvas data-glyph="${p.glyph}" width="40" height="40"></canvas><b>${p.name}</b></button>`)
    parts.push('</div>')
    if (disclord) {
      parts.push(`<div class="lobby-title" style="margin-top:14px">IMPRINTS — LVL ${disclord.level} · ${disclord.slotsOpen} slot${disclord.slotsOpen === 1 ? '' : 's'} open · ${disclord.imprintTokens} imprint token${disclord.imprintTokens === 1 ? '' : 's'}</div><div class="lobby-note">filling an empty slot and clearing are free; replacing an occupied one costs an imprint token (earned by clearing a run)</div>`)
      for (const index of [2, 3] as const) {
        const slot = kioskSlot(disclord, index)
        if (slot.locked) { parts.push(`<div class="lobby-note">SLOT ${index} — opens at level ${slot.opensAt}</div>`); continue }
        parts.push(`<div class="lobby-team">SLOT ${index}${slot.current ? ` — ${powerUpById(slot.current)?.name ?? slot.current}` : ' — empty'}</div><div class="lobby-grid">`)
        parts.push(`<button class="card ${slot.current === null ? 'on' : ''}" data-slot="${index}" data-id=""><b>NONE</b></button>`)
        for (const c of slot.cells) {
          const dis = c.state === 'locked' || c.state === 'incompatible'
          const sub = c.state === 'locked' ? `LVL ${c.unlockLevel}` : c.state === 'incompatible' ? `INCOMPATIBLE: ${c.clashing.map((k) => k.toLowerCase().replace(/_/g, ' ')).join(', ')}` : c.inert ? 'NO EFFECT YET — values pending' : ''
          parts.push(`<button class="card ${c.state === 'slotted' ? 'on' : ''} ${dis ? 'dim' : ''}" data-slot="${index}" data-id="${c.id}" ${dis ? 'disabled' : ''} title="${sub}">${c.glyph ? `<canvas data-glyph="${c.glyph}" width="40" height="40"></canvas>` : ''}<b>${c.name}</b>${sub ? `<i>${sub}</i>` : ''}</button>`)
        }
        parts.push('</div>')
      }
    }
    parts.push('<div class="lobby-note">Esc closes</div>')
    this.root.innerHTML = parts.join('')
    for (const c of Array.from(this.root.querySelectorAll<HTMLCanvasElement>('canvas[data-glyph]'))) { const ctx = c.getContext('2d'); if (ctx) drawGlyph(ctx, c.dataset.glyph as never, 20, 20, 34) }
    for (const b of Array.from(this.root.querySelectorAll<HTMLButtonElement>('button[data-load]'))) b.onclick = () => { this.onChange(b.dataset.load ? [b.dataset.load] : []); this.root.style.display = 'none' }
    for (const b of Array.from(this.root.querySelectorAll<HTMLButtonElement>('button[data-slot]'))) b.onclick = () => { this.onSlot(Number(b.dataset.slot) as 2 | 3, b.dataset.id || null); this.root.style.display = 'none' }
  }
}

/** 12 §4 run cards — the deck-of-3 UI reused: pick one temporary extra slot for the run (RUN_START / CARD stages). */
export class RunCardUI {
  onPick: (id: string) => void = () => {}
  private shownFor = ''
  constructor(private readonly root: HTMLElement) { root.style.display = 'none' }
  get visible(): boolean { return this.root.style.display !== 'none' }
  hide(): void { this.root.style.display = 'none'; this.shownFor = '' }
  update(run: RunStateMsg | null): void {
    const picking = !!run && (run.stage === 'RUN_START' || run.stage === 'CARD') && run.offered.length > 0
    if (!picking) { if (this.visible) this.hide(); return }
    const title = `${run!.stage === 'RUN_START' ? 'RUN CARD' : `BOUT ${run!.bout} CLEARED — RUN CARD`} · pick 1 · ${Math.ceil(run!.timeLeftMs / 1000)}s`
    const key = `${run!.runId}:${run!.stage}:${run!.bout}:${run!.offered.join(',')}`
    if (this.visible && this.shownFor === key) { const t = this.root.querySelector('.lobby-title'); if (t) t.textContent = title; return }
    this.shownFor = key
    this.root.style.display = ''
    const parts = [`<div class="lobby-title">${title}</div><div class="lobby-grid">`]
    for (const id of run!.offered) { const p = powerUpById(id); parts.push(`<button class="card" data-run-card="${id}">${p ? `<canvas data-glyph="${p.glyph}" width="40" height="40"></canvas>` : ''}<b>${p?.name ?? id}</b>${p ? `<i>+ ${fmt(p.buff)}</i><i>- ${fmt(p.nerf)}</i>` : '<i>no overlay yet</i>'}</button>`) }
    parts.push(`</div><div class="lobby-note">cards: ${run!.cards.map((c) => powerUpById(c)?.name ?? c).join(', ') || '—'} · a card lasts this run only; it must not clash with your imprints or earlier cards</div>`)
    this.root.innerHTML = parts.join('')
    for (const c of Array.from(this.root.querySelectorAll<HTMLCanvasElement>('canvas[data-glyph]'))) { const ctx = c.getContext('2d'); if (ctx) drawGlyph(ctx, c.dataset.glyph as never, 20, 20, 34) }
    for (const b of Array.from(this.root.querySelectorAll<HTMLButtonElement>('button[data-run-card]'))) b.onclick = () => { this.onPick(b.dataset.runCard!); this.hide() }
  }
}
